import { useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { computePortfolioDeadlines } from '../utils/agmDeadlineEngine';

/**
 * useDashboard — derives dashboard KPIs, alerts, and filing progress
 * directly from the companies already loaded in AppContext.
 *
 * All computation is client-side via the shared AGM Deadline Engine.
 * No additional API call needed — the hook re-computes when companies change.
 */
export const useDashboard = () => {
  const { companies, loading: companiesLoading } = useApp();

  /** Portfolio-level deadline aggregation */
  const portfolio = useMemo(
    () => computePortfolioDeadlines(companies),
    [companies],
  );

  /** Stats for KPI cards */
  const stats = useMemo(() => ({
    fullyCompliant: portfolio.stats.compliant,
    dueThisMonth: portfolio.stats.dueThisMonth,
    overdueCritical: portfolio.stats.overdue,
    totalCompanies: portfolio.stats.total,
    pendingSetup: portfolio.stats.pendingSetup,
    lastSyncAt: new Date().toISOString(),
  }), [portfolio.stats]);

  /** Sorted alerts for the Critical Alerts panel */
  const alerts = useMemo(() => portfolio.alerts.map((a) => ({
    id: a.id,
    companyName: a.companyName,
    companyCity: a.companyCity || '',
    companyState: a.companyState || '',
    item: a.item,
    formCode: a.formCode,
    dueDate: a.dueDate,
    daysOverdue: a.daysRemaining,
    penalty: a.penalty,
    priority: a.priority,
    estimated: a.estimated || false,
    agmNotSet: a.agmNotSet || false,
    agmNotSetForCurrentFy: a.agmNotSetForCurrentFy || false,
  })), [portfolio.alerts]);

  /** Filing progress: % of companies that have filed key forms on time */
  const filingProgress = useMemo(() => {
    if (!portfolio.results.length) return [];

    // One bar per obligation, not per form variant: the annual return is MGT-7
    // or MGT-7A depending on the company's size (Sec 2(85)), and the financial
    // statement is AOC-4 or AOC-4 XBRL — both count towards the same bar.
    const bars = [
      { label: 'AOC-4', codes: ['AOC-4', 'AOC-4 XBRL'] },
      { label: 'MGT-7 / 7A', codes: ['MGT-7', 'MGT-7A'] },
      { label: 'DIR-3 KYC', codes: ['DIR-3 KYC'] },
      { label: 'LLP Form 11', codes: ['LLP Form 11'] },
    ];
    return bars.map(({ label, codes }) => {
      let applicable = 0;
      let compliantCount = 0;

      for (const r of portfolio.results) {
        const allItems = [...r.deadlines, ...(r.agmDeadline ? [r.agmDeadline] : [])];
        const match = allItems.find((d) => codes.includes(d.code));
        if (!match) continue;
        applicable++;
        // Only count as compliant if status is 'compliant' (not overdue, not estimated-overdue)
        if (match.status === 'compliant') compliantCount++;
      }

      const pct = applicable > 0 ? Math.round((compliantCount / applicable) * 100) : 0;
      return { label, value: pct, applicable, compliant: compliantCount };
    }).filter((item) => item.applicable > 0);
  }, [portfolio.results]);

  return {
    stats,
    alerts,
    filingProgress,
    portfolio,
    loading: companiesLoading,
    error: null,
    refresh: () => {}, // companies refresh is handled by AppContext
  };
};
